import { useTranslation } from "react-i18next";
import { i18n } from "../App";

const processMilestoneAuto = (
  milestone: {
    key: string;
    param1: string;
    param2?: string;
    param3?: string;
    param4?: string;
    param5?: string;
    param6?: string;
    param7?: string;
    grand_prix_event_id: number;
    catalog: number;
  },
  t: any,
) => {
  const formatList = (value?: string, couldBeCountry?: boolean) => {
    if (!value) return value;

    const items = value
      .split(" | ")
      .map((v) => (couldBeCountry ? t(`countries:${v.trim()}`) : v.trim()))
      .filter(Boolean);

    return new Intl.ListFormat(i18n.language, {
      style: "long",
      type: "conjunction",
    }).format(items);
  };

  const translationValues: Record<string, string> = {
    param1: formatList(milestone.param1)!,
  };
  const plural =
    milestone.param1 != null && milestone.param1.split(" | ").length > 1
      ? "_plural"
      : "";
  const datesP4 = [
    "PoleAfterTime",
    "RaceAfterTime",
    "WinAfterTime",
    "PodiumAfterTime",
    "FastestLapAfterTime",
    "PointsAfterTime",
    "HatTricksAfterTime",
    "GrandChelemAfterTime",
    "RetAfterTimeWithRecord",
    "RetAfterTime",
  ];
  const daysP2 = [
    "PoleAfterTime",
    "RaceAfterTime",
    "WinAfterTime",
    "PodiumAfterTime",
    "FastestLapAfterTime",
    "PointsAfterTime",
    "HatTricksAfterTime",
    "GrandChelemAfterTime",
  ];
  const lengthP1 = [
    "MinRaceTime1",
    "MinRaceTime2",
    "MinRaceTime3",
    "MaxRaceTime1",
    "MaxRaceTime2",
    "MaxRaceTime3",
  ];
  if (milestone.param2)
    translationValues.param2 = formatList(milestone.param2)!;
  if (milestone.param3)
    translationValues.param3 = formatList(milestone.param3)!;
  if (milestone.param4)
    translationValues.param4 = formatList(milestone.param4)!;
  if (milestone.param5)
    translationValues.param5 = formatList(milestone.param5)!;
  if (milestone.param6)
    translationValues.param6 = formatList(milestone.param6)!;
  if (milestone.param7)
    translationValues.param7 = formatList(milestone.param7)!;

  if (milestone.param2) {
    if (daysP2.includes(milestone.key)) {
      translationValues.param2 = formatDaysToFriendlyText(milestone.param2, t);
    } else {
      translationValues.param2 = formatList(milestone.param2, true)!;
    }
  }
  if (lengthP1.includes(milestone.key)) {
    translationValues.param1 = convertRaceTime(translationValues.param1);
  }
  if (milestone.param3) {
    translationValues.param3 = formatList(milestone.param3)!;
  }

  if (milestone.param4) {
    if (datesP4.includes(milestone.key)) {
      translationValues.param4 = formatDateLocalized(milestone.param4);
    } else {
      translationValues.param4 = formatList(milestone.param4, true)!;
    }
  }

  translationValues.param2 = t(`countries:${translationValues.param2}`);
  translationValues.param4 = t(`countries:${translationValues.param4}`);

  return t(`milestones:${milestone.key}${plural}`, translationValues);
};

const convertRaceTime = (timeStr: string) => {
  // timeStr viene en formato XX:XX.XXX (ej: "01:23.456" o "125:45.123")
  const [minutesStr, rest] = timeStr.split(":");
  if (!rest) return timeStr; // Fallback por si el formato no es el esperado

  const [secondsStr, millisStr] = rest.split(".");

  const totalMinutes = parseInt(minutesStr, 10);
  const seconds = parseInt(secondsStr, 10);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Puedes adaptar los textos o pasarlos a tus archivos de traducción según el idioma
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} hora${hours > 1 ? "s" : ""}`);
  }

  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes} minuto${minutes !== 1 ? "s" : ""}`);
  }

  // Siempre incluimos los segundos (puedes ajustar si quieres omitirlos si son 0)
  parts.push(`${seconds} segundo${seconds !== 1 ? "s" : ""}`);

  // Formatear con "y" antes del último elemento si hay más de uno
  if (parts.length > 1) {
    const last = parts.pop();
    return `${parts.join(", ")} y ${last}`;
  }

  return parts[0] || `${seconds} segundos`;
};
const formatDaysToFriendlyText = (daysStr: string, t: any) => {
  const days = parseInt(daysStr, 10);
  if (isNaN(days)) return daysStr;

  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  const months = Math.floor(remainingDays / 30);
  if (years > 0) {
    // Si los meses son mayores a 6 -> "más de X año y medio después"
    if (months > 6) {
      return t("milestones:time.years_and_half", { count: years });
    }
    // Si está entre 1 y 6 meses, o tiene días sueltos (> 0) -> "más de X año después"
    else if (months >= 1 || remainingDays > 0) {
      return t("milestones:time.years_exact", { count: years });
    }

    // Si es exacto (365 o 730 días clavados sin resto)
    return t("milestones:time.years_exact", { count: years });
  }
  const weeks = Math.floor(days / 7);
  if (weeks > 0) {
    return t("milestones:time.weeks", { count: weeks });
  }
  return t("milestones:time.days", { count: days });
};

// 2. Función para formatear fechas usando el idioma actual de i18n
const formatDateLocalized = (dateStr: string) => {
  if (!dateStr) return dateStr;

  let date: Date;

  // Si el string viene en formato YYYY-MM-DD exacto
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // En JavaScript los meses van de 0 (Enero) a 11 (Diciembre)
    const day = parseInt(parts[2], 10);
    date = new Date(year, month, day);
  } else {
    // Por si viene en otro formato (ej. ISO completo con hora)
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr;

  return new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};
export { processMilestoneAuto };
